
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function PromoBanner() {
  const [visible, setVisible] = useState(true);
  const [timeLeft, setTimeLeft] = useState({ hours: 23, minutes: 47, seconds: 59 });

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        let { hours, minutes, seconds } = prev;
        if (seconds > 0) return { hours, minutes, seconds: seconds - 1 };
        if (minutes > 0) return { hours, minutes: minutes - 1, seconds: 59 };
        if (hours > 0) return { hours: hours - 1, minutes: 59, seconds: 59 };
        return { hours: 0, minutes: 0, seconds: 0 };
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden"
        >
          {/* Fond dégradé animé */}
          <div className="relative bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-600 py-5 px-6">
            {/* Effet de brillance animé */}
            <motion.div
              animate={{ x: ['−100%', '200%'] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear', repeatDelay: 2 }}
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 pointer-events-none"
            />

            <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Gauche : badge + texte */}
              <div className="flex items-center gap-4">
                <motion.span
                  animate={{ scale: [1, 1.08, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="bg-white text-emerald-700 text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full whitespace-nowrap shadow"
                >
                  🔥 Offre limitée
                </motion.span>
                <div className="text-white">
                  <p className="font-bold text-base leading-tight">
                    <span className="text-yellow-300 font-black">−40% sur tous les plans</span>
                    {' '}— Profitez de l'offre de lancement avant qu'elle expire !
                  </p>
                  <p className="text-emerald-100 text-xs mt-0.5">
                    Essai gratuit 14 jours inclus · Sans engagement · Annulation à tout moment
                  </p>
                </div>
              </div>

              {/* Centre : compte à rebours */}
              <div className="flex items-center gap-2">
                <span className="text-emerald-100 text-xs font-medium whitespace-nowrap">Expire dans :</span>
                <div className="flex items-center gap-1">
                  {[pad(timeLeft.hours), pad(timeLeft.minutes), pad(timeLeft.seconds)].map((val, i) => (
                    <span key={i} className="flex items-center gap-1">
                      <span className="bg-white/20 backdrop-blur text-white font-black text-sm px-2 py-1 rounded-md min-w-[36px] text-center tabular-nums">
                        {val}
                      </span>
                      {i < 2 && <span className="text-white font-bold text-sm">:</span>}
                    </span>
                  ))}
                </div>
                <div className="flex gap-1 text-emerald-200 text-[10px] font-medium">
                  <span className="w-9 text-center">h</span>
                  <span className="w-9 text-center">min</span>
                  <span className="w-9 text-center">sec</span>
                </div>
              </div>

              {/* Droite : CTA + fermer */}
              <div className="flex items-center gap-3">
                <motion.a
                  href="#pricing"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className="bg-white text-emerald-700 font-black text-sm px-5 py-2 rounded-full shadow-lg whitespace-nowrap hover:bg-yellow-50 transition-colors"
                >
                  J'en profite →
                </motion.a>
                <button
                  onClick={() => setVisible(false)}
                  className="text-white/70 hover:text-white transition-colors cursor-pointer p-1"
                  aria-label="Fermer"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
